import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  Movie,
  MoviesQueryParams,
  MoviesData,
  CreateMovieRequest,
  UpdateMovieRequest,
  MovieDetailsResponse,
  InitiateMultipartUploadRequest,
  InitiateMultipartUploadResponse,
  CompleteMultipartUploadRequest,
  VideoVariant,
  VideoMultipartStatusResponse,
  VideoMultipartRegenerateRequest,
  VideoMultipartPresignResponse,
  PreviewImageUpdateRequest,
  PreviewImageResponse,
  PreviewImageBatchRequest,
} from '../../types/api/movie';

/**
 * Movie Service
 * 
 * Handles all movie-related API operations for admin functionality
 * 
 * Features:
 * - Movie listing with pagination and filtering
 * - Movie CRUD operations
 * - Form-data upload support for thumbnails
 * - Search functionality
 */
export class MovieService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  /**
   * Get movies list with pagination and filters
   */
  async getMovies(params: MoviesQueryParams = {}): Promise<MoviesData> {
    const queryParams = new URLSearchParams();
    
    // Add pagination parameters
    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }
    if (params.size !== undefined) {
      queryParams.append('size', params.size.toString());
    }
    
    // Add filter parameters
    if (params.keyword) {
      queryParams.append('keyword', params.keyword);
    }
    if (params.languageId) {
      queryParams.append('languageId', params.languageId);
    }
    if (params.status) {
      queryParams.append('status', params.status);
    }
    if (params.accessLevel) {
      queryParams.append('accessLevel', params.accessLevel);
    }
    if (params.contentRegion) {
      queryParams.append('contentRegion', params.contentRegion);
    }

    const url = `${API_ENDPOINTS.MOVIES.ADMIN_LIST}?${queryParams.toString()}`;
    const response = await this.httpClient.get<MoviesData>(url);
    
    // Check if API request was successful
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch movies');
    }
    
    return response.data;
  }

  /**
   * Get movie by ID with genres and details
   * Uses public endpoint: /public/movies/:movieId
   */
  async getMovie(movieId: string): Promise<MovieDetailsResponse> {
    const response = await this.httpClient.get<MovieDetailsResponse>(`/public/movies/${movieId}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch movie');
    }
    
    // The API response structure is: { success, message, data: MovieDetailsResponse, timestamp }
    // HttpClient.get returns ApiResponse<T> where T is MovieDetailsResponse
    // So response.data is already MovieDetailsResponse
    return response.data;
  }

  /**
   * Create a new movie with thumbnail upload
   * Uses form-data with 'data' (JSON string) and 'thumbnail' (File)
   */
  async createMovie(
    movieData: CreateMovieRequest,
    thumbnailFile?: File
  ): Promise<Movie> {
    // Create FormData
    const formData = new FormData();
    
    // Add JSON data with explicit content type so backend doesn't treat it as octet-stream
    const jsonBlob = new Blob([JSON.stringify(movieData)], { type: 'application/json' });
    formData.append('data', jsonBlob, 'data.json');
    
    // Add thumbnail file if provided
    if (thumbnailFile) {
      // Attach file with its original filename so servers recognize the part correctly
      formData.append('thumbnail', thumbnailFile, thumbnailFile.name);
    }

    // Use request method for form-data upload
    // HttpClient will automatically handle FormData and set proper Content-Type
    const response = await this.httpClient.request<Movie>({
      method: 'POST',
      url: '/admin/movies',
      data: formData,
      headers: {
        // Explicitly set multipart to satisfy servers/gateways that require it
        'Content-Type': 'multipart/form-data',
      },
    });
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to create movie');
    }
    
    return response.data;
  }

  /**
   * Update movie information
   */
  async updateMovie(
    movieId: string,
    updateData: UpdateMovieRequest
  ): Promise<Movie> {
    const response = await this.httpClient.put<Movie>(
      `/admin/movies/${movieId}`,
      updateData
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to update movie');
    }
    
    return response.data;
  }

  /**
   * Replace movie thumbnail using the dedicated thumbnail endpoint.
   *
   * Backend endpoint: PUT /api/admin/movies/{movieId}/thumbnail
   * Content-Type: multipart/form-data
   * Parts:
   *  - thumbnail: file (required)
   */
  async updateThumbnail(movieId: string, thumbnailFile: File): Promise<Movie> {
    const formData = new FormData();
    formData.append('thumbnail', thumbnailFile, thumbnailFile.name);

    const response = await this.httpClient.request<Movie>({
      method: 'PUT',
      url: API_ENDPOINTS.MOVIES.ADMIN_UPDATE_THUMBNAIL(movieId),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update movie thumbnail');
    }

    return response.data;
  }

  /**
   * Delete a movie
   */
  async deleteMovie(movieId: string): Promise<void> {
    const response = await this.httpClient.delete<{ message: string }>(
      `/admin/movies/${movieId}`
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete movie');
    }
  }

  /**
   * Initiate multipart video upload for a movie
   */
  async initiateMultipartVideoUpload(
    contentId: string,
    payload: InitiateMultipartUploadRequest
  ): Promise<InitiateMultipartUploadResponse> {
    const response = await this.httpClient.post<InitiateMultipartUploadResponse>(
      `/admin/content/${contentId}/videos/initiate-multipart`,
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to initiate video upload');
    }

    return response.data;
  }

  /**
   * Complete multipart video upload for a movie
   */
  async completeMultipartVideoUpload(
    contentId: string,
    payload: CompleteMultipartUploadRequest
  ): Promise<VideoVariant> {
    const response = await this.httpClient.post<VideoVariant>(
      `/admin/content/${contentId}/videos/complete-multipart`,
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to complete video upload');
    }

    return response.data;
  }

  /**
   * Abort multipart video upload session
   */
  async abortMultipartVideoUpload(params: { key: string; uploadId: string }): Promise<void> {
    const queryParams = new URLSearchParams({
      key: params.key,
      uploadId: params.uploadId,
    });

    const response = await this.httpClient.delete<{ message: string }>(
      `/admin/content/videos/abort?${queryParams.toString()}`
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to abort video upload');
    }
  }

  /**
   * Get the current status of an in-progress multipart upload.
   * Returns which parts are already uploaded to storage.
   */
  async getMultipartUploadStatus(
    contentId: string,
    key: string,
    uploadId: string
  ): Promise<VideoMultipartStatusResponse> {
    const queryParams = new URLSearchParams({
      key,
      uploadId,
    });

    const response = await this.httpClient.get<VideoMultipartStatusResponse>(
      `/admin/content/${contentId}/videos/multipart-status?${queryParams.toString()}`
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch multipart upload status');
    }

    return response.data;
  }

  /**
   * Regenerate presigned URLs for specific parts of an existing multipart upload.
   * Used when presigned URLs expire or when retrying failed parts.
   */
  async regenerateMultipartPartUrls(
    contentId: string,
    request: VideoMultipartRegenerateRequest
  ): Promise<VideoMultipartPresignResponse> {
    const response = await this.httpClient.post<VideoMultipartPresignResponse>(
      `/admin/content/${contentId}/videos/regenerate-part-urls`,
      request
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to regenerate multipart part URLs');
    }

    return response.data;
  }

  /**
   * Delete a video variant from a movie
   * 
   * @param contentId - The content/movie ID
   * @param fileId - The video file ID to delete
   */
  async deleteVideo(contentId: string, fileId: string): Promise<void> {
    const response = await this.httpClient.delete<{ message: string }>(
      `/admin/content/${contentId}/videos/${fileId}`
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete video');
    }
  }

  /**
   * Upload preview images for a movie (batch, up to 5 per request).
   *
   * Backend endpoint: POST /api/admin/movies/{movieId}/preview-images
   * Expects multipart/form-data with:
   *  - files: MultipartFile[] (required, 1..5)
   *  - metadata: JSON(PreviewImageBatchRequest) (optional)
   */
  async uploadPreviewImages(
    movieId: string,
    files: File[],
    metadata?: PreviewImageBatchRequest
  ): Promise<PreviewImageResponse[]> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    if (metadata && metadata.metadata && metadata.metadata.length > 0) {
      const metadataBlob = new Blob([JSON.stringify(metadata)], {
        type: 'application/json',
      });
      formData.append('metadata', metadataBlob, 'metadata.json');
    }

    const response = await this.httpClient.request<PreviewImageResponse[]>({
      method: 'POST',
      url: `/admin/movies/${movieId}/preview-images`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to upload preview images');
    }

    return response.data;
  }

  /**
   * Update preview image metadata for a movie (JSON only).
   *
   * Backend endpoint: PUT /api/admin/movies/{movieId}/preview-images/{previewImageId}
   * Content-Type: application/json
   */
  async updatePreviewImageMetadata(
    movieId: string,
    previewImageId: string,
    payload: PreviewImageUpdateRequest
  ): Promise<PreviewImageResponse> {
    const response = await this.httpClient.put<PreviewImageResponse>(
      `/admin/movies/${movieId}/preview-images/${previewImageId}`,
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to update preview image metadata');
    }

    return response.data;
  }

  /**
   * Update preview image metadata with optional file replacement for a movie.
   *
   * Backend endpoint: PUT /api/admin/movies/{movieId}/preview-images/{previewImageId}
   * Content-Type: multipart/form-data
   * Parts:
   *  - data: JSON (PreviewImageUpdateRequest) (required)
   *  - file: MultipartFile (optional)
   */
  async updatePreviewImageWithFile(
    movieId: string,
    previewImageId: string,
    payload: PreviewImageUpdateRequest,
    file?: File
  ): Promise<PreviewImageResponse> {
    const formData = new FormData();

    const dataBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', dataBlob, 'data.json');

    if (file) {
      formData.append('file', file, file.name);
    }

    const response = await this.httpClient.request<PreviewImageResponse>({
      method: 'PUT',
      url: `/admin/movies/${movieId}/preview-images/${previewImageId}`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update preview image');
    }

    return response.data;
  }
}
